import { createSelector } from 'reselect';
import merge from 'lodash/merge';

import { selectFaceliftEnabled, selectIsWebSDK, selectTargetPlatform } from '@pressreader/appcore';
import { OperationStatus } from '@pressreader/appstore';
import { selectAuthInProgress, selectUser, selectUserIsAuthenticated } from '@pressreader/authentication-core';
import { selectCurrentSubscription } from '@pressreader/commerce';
import { selectGetConfigValue } from '@pressreader/config';
import { selectAskViewGlobalConfig, selectBooksGlobalConfig } from '@pressreader/global-config';
import { selectIsArticlePopupRouteOpen, selectViewName } from '@pressreader/navigation-core';
import { selectDialogPopups, selectIsAnyModalOpened } from '@pressreader/popups-core';
import { selectDialogPopups as selectLegacyDialogPopups, selectIsLegacyModalOpened } from '@pressreader/popups-legacy';
import { getSponsorshipStatus, selectIsEmbeddedModeEnabled } from '@pressreader/sponsorship-core';
import { SponsorshipStatus } from '@pressreader/sponsorship-types';
import { TargetPlatform, toBool } from '@pressreader/types';
import { selectIsCompactView } from '@pressreader/ui-common';

import { INavMenuConfigDto } from '../api/types';
import { FEATURE_FLAG_HOMEFEED_ENABLED, MAX_FONT_SCALE, MIN_FONT_SCALE, MODULE_NAME } from '../constants';
import { IGenericSiteNavConfig } from '../types/generic.site.nav.config';
import { TranslationConfig } from '../types/translation.config';

import { IState } from './reducer';

const getState = (state: { [MODULE_NAME]: IState }) => state[MODULE_NAME];

const selectGenericSiteNavConfig = createSelector(
    selectGetConfigValue,
    selectUserIsAuthenticated,
    selectBooksGlobalConfig,
    selectIsEmbeddedModeEnabled,
    selectFaceliftEnabled,
    selectAskViewGlobalConfig,
    selectIsWebSDK,
    (getConfigValue, userAuthenticated, booksConfig, isEmbeddedModeEnabled, isFacelift, askViewConfig, isWebSDK): IGenericSiteNavConfig => {
        const defaultConfig: IGenericSiteNavConfig = {
            about: { enabled: false, location: null },
            blog: { enabled: false, location: null },
            help: { enabled: false, location: null },
            topnews: { enabled: true },
            books: { enabled: booksConfig.enabled },
            exitPublication: { enabled: isFacelift },
            askView: { enabled: askViewConfig.showTopBarItem },
        };

        const navMenuConfigDto = getConfigValue<INavMenuConfigDto>('app.views.common.nav_menu');

        if (!navMenuConfigDto) {
            return defaultConfig;
        }

        const config: INavMenuConfigDto = merge(defaultConfig, navMenuConfigDto);

        return {
            about: {
                enabled: config.about.enabled,
                location: config.about.location,
            },
            blog: {
                enabled: config.blog.enabled,
                location: config.blog.location,
            },
            help: {
                enabled: config.help.enabled,
                location: config.help.location,
            },
            topnews: {
                enabled: config.topnews.enabled && (isWebSDK || !isEmbeddedModeEnabled) && getConfigValue(FEATURE_FLAG_HOMEFEED_ENABLED, true),
            },
            books: {
                enabled: booksConfig.enabled,
            },
            exitPublication: {
                enabled: isFacelift,
            },
            askView: { enabled: askViewConfig.showTopBarItem && userAuthenticated },
        };
    },
);

const selectAvailableFonts = createSelector(getState, state => state.fonts);
// Simple passthrough selector for top bar menu items
const selectDynamicTopBarMenuItems = createSelector(getState, state => state.topBarMenuItems);

const selectCurrentFontId = createSelector(getState, state => state.currentFontId);

const selectCurrentFontScale = createSelector(getState, state => state.currentFontScale);

const selectCurrentLineSpacing = createSelector(getState, state => state.currentLineSpacing);

const selectCanIncreaseFontScale = createSelector(selectCurrentFontScale, currentScale => currentScale < MAX_FONT_SCALE);
const selectCanDecreaseFontScale = createSelector(selectCurrentFontScale, currentScale => currentScale > MIN_FONT_SCALE);

const selectLegacyAppStatus = createSelector(getState, state => state.legacyAppStatus);

const selectIsGlobalLoading = createSelector(
    selectViewName,
    selectLegacyAppStatus,
    selectLegacyDialogPopups,
    selectDialogPopups,
    (viewName, legacyAppStatus, legacyDialogs, dialogs) => {
        // show global spinner only if app wasn't initialized, legacy app loaded
        // or a first view is in NavigateBegan state without opened dialogs

        const viewLoading = !viewName && !legacyDialogs.length && !dialogs.length;
        return legacyAppStatus === OperationStatus.Done ? false : viewLoading;
    },
);

const selectIsNonInteractiveLoading = createSelector(
    selectAuthInProgress,
    selectLegacyAppStatus,
    (isAuthInProgress, legacyAppStatus) => isAuthInProgress || legacyAppStatus === OperationStatus.Pending,
);

const selectIsGlobalScrollEnabled = createSelector(selectLegacyDialogPopups, selectDialogPopups, (legacyDialogs, dialogs) => {
    return (
        (!legacyDialogs.length || !legacyDialogs[legacyDialogs.length - 1].supportNativeScroll) &&
        (!dialogs.length || !dialogs[dialogs.length - 1].supportNativeScroll)
    );
});

const selectRootPanelClassNames = createSelector(selectGetConfigValue, getConfigValue =>
    getConfigValue<string>('app.common.rootPanelClassName', '')
        .split(' ')
        .filter(v => v),
);

const selectFeedbackFormEnabled = createSelector(
    selectGetConfigValue,
    selectFaceliftEnabled,
    (getConfigValue, isFaceliftEnabled) => isFaceliftEnabled && getConfigValue<boolean>('features.feedback.form.enabled', false),
);

const selectFeedbackFormUrl = createSelector(selectGetConfigValue, getConfigValue => getConfigValue<string>('features.feedback.form.url', ''));

const selectGenericFooterContext = createSelector(
    selectTargetPlatform,
    selectIsCompactView,
    selectGetConfigValue,
    (platform, isCompactView, getConfigValue) => ({
        showFooterContent: getConfigValue<boolean>('features.views.footer.generic.showContent', true),
        showFooterLinks: platform !== TargetPlatform.Voyager,
        mobileView: isCompactView,
    }),
);

export const selectTextViewTranslationConfig = createSelector(selectGetConfigValue, (getConfigValue): TranslationConfig => {
    const dto = getConfigValue<{
        enableTextViewTranslation: {
            enableForAnonymous: boolean | string;
            enableForSponsored: boolean | string;
            enableForPremium: boolean | string;
            enableForRegistered: boolean | string;
            value: boolean | string;
        };
    }>('translation');

    return {
        enable: toBool(dto?.enableTextViewTranslation.value),
        enableForAnonymous: toBool(dto?.enableTextViewTranslation.enableForAnonymous),
        enableForSponsored: toBool(dto?.enableTextViewTranslation.enableForSponsored),
        enableForPremium: toBool(dto?.enableTextViewTranslation.enableForPremium),
        enableForRegistered: toBool(dto?.enableTextViewTranslation.enableForRegistered),
    };
});

export const selectIsTranslationEnabled = createSelector(
    selectTargetPlatform,
    selectUser,
    selectCurrentSubscription,
    getSponsorshipStatus,
    selectTextViewTranslationConfig,
    (platform, user, subscription, sponsorStatus, translationConfig) => {
        if (platform === TargetPlatform.Voyager) {
            return false;
        }
        if (sponsorStatus === SponsorshipStatus.On) {
            return translationConfig.enableForSponsored;
        }
        if (!user.isAuthenticated) {
            return translationConfig.enableForAnonymous;
        }
        if (subscription && subscription.isUnlimited) {
            return translationConfig.enableForPremium;
        }
        if (subscription && subscription.isFree) {
            return translationConfig.enableForRegistered;
        }

        return translationConfig.enable;
    },
);

/**
 * Selector to determine if the content width limiting is enabled for views a CSS class is to be added to the root panel.
 */
const selectIsViewsContentWidthLimited = createSelector(selectGetConfigValue, getConfigValue =>
    getConfigValue<boolean>('features.views.common.content.width.limited.enabled', false),
);

/**
 * Selector that returns true when any popup/modal is open in popup state
 * or in navigation state.
 *
 * Sources:
 * - legacy popups (dialogs and context menus),
 * - FaceLift popups (including baseline popups),
 * - article popup route in navigation state (route flag, not render flag).
 */
const selectIsAnyModalDialogOnScreen = createSelector(
    selectIsLegacyModalOpened,
    selectIsAnyModalOpened,
    selectIsArticlePopupRouteOpen,
    (isLegacyModalsOpen, isFaceLiftModalOpened, isArticlePopupRouteOpen) => isLegacyModalsOpen || isFaceLiftModalOpened || isArticlePopupRouteOpen,
);

export {
    selectAvailableFonts,
    selectCanDecreaseFontScale,
    selectCanIncreaseFontScale,
    selectCurrentFontId,
    selectCurrentFontScale,
    selectCurrentLineSpacing,
    selectDynamicTopBarMenuItems,
    selectFeedbackFormEnabled,
    selectFeedbackFormUrl,
    selectGenericFooterContext,
    selectGenericSiteNavConfig,
    selectIsAnyModalDialogOnScreen,
    selectIsGlobalLoading,
    selectIsGlobalScrollEnabled,
    selectIsNonInteractiveLoading,
    selectIsViewsContentWidthLimited,
    selectLegacyAppStatus,
    selectRootPanelClassNames,
};
